const PRESENTATION_SEND_COOLDOWN_MS = 60_000; // 1 minute
const STORAGE_KEY_PREFIX = "offer-experience:presentation-sent-at:";
const DUPLICATE_MESSAGE = "Duplicate message within 60 seconds";

const duplicatePresentationSendMessages = {
  offer: "Offer already sent. You need to wait 1 minute to re-send it",
  offers: "Offers already sent. You need to wait 1 minute to re-send it",
  cap: "CAP presentation already sent. You need to wait 1 minute to re-send it",
} as const;

const storageKey = (prospectId: string) => `${STORAGE_KEY_PREFIX}${prospectId}`;

type DuplicateErrorShape = {
  message?: unknown;
  cause?: unknown;
  response?: {
    status?: number;
    statusCode?: number;
    data?: {
      duplicate?: unknown;
      error?: unknown;
      message?: unknown;
    };
  };
};

const getStorage = (): Storage | undefined => {
  try {
    return globalThis.localStorage;
  } catch {
    return undefined;
  }
};

const getLastPresentationSendAt = (prospectId: string): number | undefined => {
  if (!prospectId) {
    return undefined;
  }
  try {
    const raw = getStorage()?.getItem(storageKey(prospectId));
    if (!raw) {
      return undefined;
    }
    const timestamp = Number(raw);
    return Number.isFinite(timestamp) ? timestamp : undefined;
  } catch {
    return undefined;
  }
};

const markPresentationSent = (prospectId: string, sentAt = Date.now()) => {
  if (!prospectId) {
    return;
  }
  try {
    getStorage()?.setItem(storageKey(prospectId), String(sentAt));
  } catch {
    // Ignore storage failures (private mode, quota, etc.)
  }
};

const isWithinPresentationSendCooldown = (
  prospectId: string,
  now = Date.now()
): boolean => {
  const lastSentAt = getLastPresentationSendAt(prospectId);
  if (lastSentAt === undefined) {
    return false;
  }
  return now - lastSentAt < PRESENTATION_SEND_COOLDOWN_MS;
};

const includesDuplicateMessage = (value: unknown): boolean => {
  return typeof value === "string" && value.includes(DUPLICATE_MESSAGE);
};

const isDuplicateErrorShape = (error: unknown): boolean => {
  if (!error || typeof error !== "object") {
    return false;
  }
  const { message, response } = error as DuplicateErrorShape;
  const status = response?.status ?? response?.statusCode;
  const data = response?.data;
  return (
    status === 409 ||
    data?.duplicate === true ||
    includesDuplicateMessage(message) ||
    includesDuplicateMessage(data?.error) ||
    includesDuplicateMessage(data?.message)
  );
};

const isDuplicatePresentationSendError = (error: unknown): boolean => {
  const { cause } = (error ?? {}) as DuplicateErrorShape;
  return isDuplicateErrorShape(error) || isDuplicateErrorShape(cause);
};

const getOfferDuplicatePresentationSendMessage = (offerCount: number) =>
  offerCount > 1
    ? duplicatePresentationSendMessages.offers
    : duplicatePresentationSendMessages.offer;

export {
  PRESENTATION_SEND_COOLDOWN_MS,
  duplicatePresentationSendMessages,
  getLastPresentationSendAt,
  getOfferDuplicatePresentationSendMessage,
  isDuplicatePresentationSendError,
  isWithinPresentationSendCooldown,
  markPresentationSent,
};
